import { FilterExpression, IdPathParameters, Pageable, SearchOperation } from '@infuse/api/common';
import { Role } from '@infuse/iam/role/type';
import { useInfiniteQuery } from '@tanstack/vue-query';
import { computed, ref } from 'vue';

const createSearchFilter = (searchQuery: string): FilterExpression | undefined => {
  if (searchQuery === '') {
    return;
  }

  const filterOptions = searchQuery
    .toLowerCase()
    .split(' ')
    .filter((part: string): boolean => part !== undefined && part !== null && part !== '')
    .map((part: string): string => `%${part}%`)
    .map((part: string): FilterExpression => {
      return new FilterExpression().like('lower(name)', part);
    });

  if (filterOptions.length === 0) {
    return;
  }
  return filterOptions.reduce((acc, cv) => acc.and(cv));
};

export function useGenericRoles({
  apiFn,
  queryKeys,
  pageSize = 20,
  onlyEnableWithParentId = false,
  initialParentId
}: {
  apiFn: SearchOperation<Role> | SearchOperation<Role, IdPathParameters<string>>;
  queryKeys: string[];
  pageSize?: number;
  onlyEnableWithParentId?: boolean;
  initialParentId?: string;
}) {
  const searchQuery = ref<string | undefined>();
  const fetchParentId = ref<string | undefined>(initialParentId);

  const query = useInfiniteQuery<Pageable<Role>>({
    initialPageParam: 0,
    queryKey: [...queryKeys, fetchParentId, searchQuery],
    enabled: () => (onlyEnableWithParentId ? !!fetchParentId.value : true),
    queryFn: async ({ pageParam }) => {
      return apiFn(
        {
          size: pageSize,
          page: Number(pageParam),
          filter: searchQuery.value ? createSearchFilter(searchQuery.value) : undefined,
          sort: [{ key: 'ranking' }, { key: 'name' }]
        },
        undefined,
        fetchParentId.value ? { id: fetchParentId.value } : undefined
      );
    },
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.pageable.pageNumber + 1)
  });

  const roles = computed(() => query.data.value?.pages.flatMap((page) => page.content) || []);
  const currentPage = computed(() => (query.data.value ? (query.data.value.pageParams.slice(-1)[0] as number) : 0));

  return {
    ...query,
    searchQuery,
    fetchParentId,
    currentPage,
    roles
  };
}
